import { useMutation } from "@tanstack/react-query";

import { toApiError } from "../../lib/apiError";
import { useApiClient } from "../useApiClient";
import type { components } from "../schema";

type StatementSampleResponse = components["schemas"]["StatementSampleResponse"];

export type StatementSampleVariables = {
  file: File;
  broker?: string;
};

/**
 * The raw clock faces a statement prints, for the zone picker to check against.
 *
 * A MUTATION rather than a query even though it reads: it posts a file, nothing
 * is persisted or metered server-side, and caching it by the file's identity
 * would be caching real user times under a key the browser would hold after the
 * user moved on.
 *
 * TAKES NO ACCOUNT ID, matching the endpoint. The only situation it exists for
 * is an account with no zone, so there is nothing to look up and nothing to
 * assume -- see the route's own docstring.
 *
 * **A file it cannot read is a 200 with every field null, not an error.** The
 * picker turns that into "choose a different file" and offers no zone choice, so
 * nothing here treats it as a failure.
 */
export function useStatementSample() {
  const client = useApiClient();
  return useMutation<StatementSampleResponse, Error, StatementSampleVariables>({
    mutationFn: async ({ file, broker }) => {
      const formData = new FormData();
      formData.append("file", file);
      if (broker !== undefined) {
        formData.append("broker", broker);
      }
      const { data, error, response } = await client.POST("/api/imports/statement-sample", {
        body: { file, broker: broker ?? "tos" } as unknown as {
          file: string;
          broker: string;
        },
        bodySerializer: () => formData,
      });
      if (error !== undefined) {
        // The same shared cap /validate enforces, so the same sentence.
        if (response.status === 413) {
          throw new Error("File is too large. Try a smaller CSV.");
        }
        throw new Error(toApiError(error, "Failed to read times from this statement."));
      }
      return data;
    },
  });
}
